import React from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import { MobXProviderContext, Observer } from "mobx-react";
import DialogContent from "@mui/material/DialogContent";
import Box from "@mui/material/Box";
import DialogTitle from "@mui/material/DialogTitle";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import Slide from "@mui/material/Slide";
import { Grid, TextField, FormControlLabel, Checkbox, Stack, Typography } from "@mui/material";
import { isEntregadorOnline } from "shared/entregadorUtils";

const Transition = React.forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);

// `rota` (opcional): quando informado, o dialog age sobre uma rota existente
// (aba Rotas) em vez da seleção de pedidos — igual ao SelecionarEntregadorSheet
// do Flutter. Marcado "A Caminho" = despacha a rota; desmarcado = só (re)define
// o motoca dela.
export default function SelecionarEntregadorDialog({ rota = null, label }) {
  const { mapaStore, entregadoresStore } = React.useContext(MobXProviderContext);
  const [open, setOpen] = React.useState(false);
  const [openRota, setOpenRota] = React.useState(false);

  const [entregador, setEntregador] = React.useState("");
  const [codEntregador, setCodEntregador] = React.useState("");

  // Load checkbox preference from localStorage, default to true
  const [marcarACaminho, setMarcarACaminho] = React.useState(() => {
    const saved = localStorage.getItem('marcarEntregaACaminho');
    return saved !== null ? saved === 'true' : true;
  });

  // Status de entregador que pode receber pedidos: 1 (disponível) sempre; 2
  // (em entrega / voltando pra loja) só no modo flexível (ModoDistribuicao=1).
  const statusSelecionavel = (status) =>
    status === 1 || (mapaStore.distribuicaoFlexivel && status === 2);

  const handleChange = (event) => {
    setEntregador(event.target.value);

    var index = entregadoresStore.entregadores.findIndex(entregador =>
      entregador.EntregadorId == event.target.value && statusSelecionavel(entregador.Status))

    if (index > -1) {
      setCodEntregador(entregadoresStore.entregadores[index].CodigoExternoEntregador);
    }
  };

  const handleClickOpen = () => {
    // Numa rota existente, desenha a rota dela; senão, a rota da seleção atual.
    if (rota) {
      mapaStore.calcularRotaPedidos(rota.pedidos);
    } else {
      mapaStore.makeRoute();
    }
    entregadoresStore.buscarEntregadoresAtivos().then(() => {
      // Numa rota já existente, pré-seleciona o motoca atual dela (se houver).
      if (rota && rota.entregadorId) setEntregador(rota.entregadorId);
      setOpen(true);
    });
  };

  const handleClose = () => {
    setOpen(false);
  };

  const enviar = () => {
    if (entregador === '') return;

    const entregadorId = Number(entregador);

    // Modo restritivo (padrão): EXATAMENTE o fluxo antigo (antes do commit de
    // rotas). Sem rotas, sem "adicionar em rota", sem criar rota ao desmarcar.
    if (!mapaStore.distribuicaoFlexivel) {
      mapaStore.setPedidosOrdenadosRota(mapaStore.pedidosSelecionados[0]);
      mapaStore.definirEntregadores(entregador);
      setOpen(false);
      if (marcarACaminho) {
        mapaStore.definirStatus(2, entregador);
        setOpenRota(true);
      } else {
        mapaStore.definirStatus(1, entregador);
      }
      return;
    }

    // Aba Rotas: despacha ou só (re)define o motoca da rota. Igual ao Flutter,
    // aqui não abre o "Compartilhar rota" (resultado 'ok').
    if (rota) {
      if (marcarACaminho) {
        mapaStore.despacharRota(rota, entregadorId, true);
        setOpen(false);
      } else {
        mapaStore.definirEntregadorRota(rota, entregadorId);
        setOpen(false);
      }
      return;
    }

    mapaStore.setPedidosOrdenadosRota(mapaStore.pedidosSelecionados[0]);

    // Motoca já a caminho + "A Caminho" marcado => adiciona no FIM da rota
    // dele em vez de abrir uma rota nova (modo flexível).
    if (marcarACaminho && mapaStore.entregadorEmRota(entregadorId)) {
      // Igual ao Flutter (resultado 'add'): vai pra aba A Caminho, sem link.
      mapaStore.adicionarSelecionadosAEntregadorEmRota(entregadorId);
      setOpen(false);
      mapaStore.setTabIndex(2);
      return;
    }

    setOpen(false);

    if (marcarACaminho) {
      // Despacho SEQUENCIAL (igual ao enviarParaEntregador do Flutter):
      // definirentregador -> definirstatus(2), 1 refresh no fim.
      mapaStore.enviarParaEntregador(entregadorId, true);
      setOpenRota(true);
    } else {
      // Desmarcado => cria uma ROTA com o motoca (aguardando despacho) e vai
      // pra aba Rotas (resultado 'rota' do Flutter).
      mapaStore.criarRotaComSelecionados(entregadorId);
      mapaStore.setTabIndex(1);
    }
  };

  const handleCloseRota = () => {
    setOpenRota(false);
  };

  function isInt(value) {
    return !isNaN(value) &&
      parseInt(Number(value)) == value &&
      !isNaN(parseInt(value, 10));
  }

  const handleChangeCodEntregador = (e) => {
    setCodEntregador(e.target.value);

    if (isInt(e.target.value)) {
      var index = entregadoresStore.entregadores.findIndex(entregador =>
        entregador.CodigoExternoEntregador == e.target.value && statusSelecionavel(entregador.Status))

      if (index > -1) {
        setEntregador(entregadoresStore.entregadores[index].EntregadorId);
      }
    }

  };

  const handleChangeMarcarACaminho = (event) => {
    const newValue = event.target.checked;
    setMarcarACaminho(newValue);
    localStorage.setItem('marcarEntregaACaminho', newValue.toString());
  };


  // Função para calcular tempo disponível
  const calcularTempoDisponivel = (horarioMudancaStatus) => {
    if (!horarioMudancaStatus || horarioMudancaStatus.length !== 14) return null;

    const year = parseInt(horarioMudancaStatus.substr(0, 4));
    const month = parseInt(horarioMudancaStatus.substr(4, 2)) - 1;
    const day = parseInt(horarioMudancaStatus.substr(6, 2));
    const hour = parseInt(horarioMudancaStatus.substr(8, 2));
    const minute = parseInt(horarioMudancaStatus.substr(10, 2));
    const second = parseInt(horarioMudancaStatus.substr(12, 2));

    const dataMudanca = new Date(year, month, day, hour, minute, second);
    const agora = new Date();
    const diferencaMs = agora - dataMudanca;
    const diferencaMinutos = Math.floor(diferencaMs / (1000 * 60));

    return diferencaMinutos;
  };

  // Função para obter status do entregador
  const getEntregadorStatus = (entregadorId) => {
    const pedidosPendentes = mapaStore.pedidosPendentes.filter(p =>
      p.entregador && p.entregador.EntregadorId === entregadorId
    ).length;

    const pedidosACaminho = mapaStore.pedidosACaminho.filter(p =>
      p.entregador && p.entregador.EntregadorId === entregadorId
    ).length;

    if (pedidosACaminho > 0) {
      return 'ocupado';
    }

    if (pedidosPendentes > 0) {
      return 'aguardando';
    }

    return 'disponivel';
  };

  return (
    <div>
      <Observer>
        {() => (
          <Button
            onClick={handleClickOpen}
            size={rota ? "small" : "medium"}
            disabled={
              rota
                ? mapaStore.inProgress
                : !mapaStore.pedidosSelecionados ||
                  !mapaStore.pedidosSelecionados[mapaStore.tabIndex] ||
                  mapaStore.pedidosSelecionados[mapaStore.tabIndex].length < 1
            }
          >
            {label || "Definir Entregador"}
          </Button>
        )}
      </Observer>
      <Dialog
        open={open}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleClose}
        aria-describedby="alert-dialog-slide-description"
      >
        <DialogTitle>
          {rota
            ? `Rota #${rota.numero} · ${rota.quantidade} pedido(s)`
            : "Selecionar entregador para a rota"}
        </DialogTitle>
        <DialogContent>
          {/* <DialogContentText id="selecionar-entregador">
            Lista de entregadores
          </DialogContentText> */}
          <Box sx={{ minWidth: 250 }}>
            <Grid container direction="row" spacing={1}>

              <Grid item xs={3}>
                <Observer>
                  {() => (
                    <FormControl fullWidth>
                      <TextField fullWidth
                        id="select-nome"
                        label="Código"
                        variant="standard"
                        inputRef={(input) => input && input.focus()}
                        type="number"
                        onKeyDown={(ev) => {
                          if (ev.key === 'Enter') {
                            enviar();
                            ev.preventDefault();
                          }
                        }}
                        value={codEntregador} onChange={handleChangeCodEntregador} style={{ paddingBottom: 5 }} />
                    </FormControl>
                  )}
                </Observer>
              </Grid>

              <Grid item xs={9}>
                <Observer>
                  {() => (
                    <FormControl fullWidth>
                      <Select
                        id="select-entregador"
                        value={entregador}
                        style={{ height: 45 }}
                        variant="filled"
                        onChange={handleChange}
                        size="small"
                      >
                        {entregadoresStore.entregadores
                          // No modo flexível (ModoDistribuicao=1), também lista quem
                          // está em entrega (Status 2) para permitir adicionar pedidos
                          // a um motoca já a caminho / voltando pra loja.
                          .filter((entr) =>
                            entr.Status === 1 ||
                            (mapaStore.distribuicaoFlexivel && entr.Status === 2)
                          )
                          .sort((a, b) => {
                            // Verifica se tem entregadores online
                            const aOnline = isEntregadorOnline(a.DataUltimaGeoLocalizacao);
                            const bOnline = isEntregadorOnline(b.DataUltimaGeoLocalizacao);

                            // Se apenas um está online, ele vai primeiro
                            if (aOnline && !bOnline) return -1;
                            if (!aOnline && bOnline) return 1;

                            // Se ambos estão online, ordenar por status e tempo disponível
                            if (aOnline && bOnline) {
                              const statusA = getEntregadorStatus(a.EntregadorId);
                              const statusB = getEntregadorStatus(b.EntregadorId);

                              const prioridades = {
                                'disponivel': 1,
                                'aguardando': 2,
                                'ocupado': 3
                              };

                              const prioA = prioridades[statusA] || 999;
                              const prioB = prioridades[statusB] || 999;

                              if (prioA !== prioB) {
                                return prioA - prioB;
                              }

                              // Se ambos disponíveis, ordenar por tempo (mais antigo primeiro)
                              if (statusA === 'disponivel' && statusB === 'disponivel') {
                                const tempoA = calcularTempoDisponivel(a.HorarioMudancaStatus);
                                const tempoB = calcularTempoDisponivel(b.HorarioMudancaStatus);
                                return (tempoB || 0) - (tempoA || 0);
                              }
                            }

                            // Manter ordem original para offline
                            return 0;
                          })
                          .map((entr) => {
                            const online = isEntregadorOnline(entr.DataUltimaGeoLocalizacao);
                            const status = online ? getEntregadorStatus(entr.EntregadorId) : null;

                            return (
                              <MenuItem key={entr.EntregadorId} value={entr.EntregadorId}>
                                <Stack direction="row" alignItems="center" spacing={1} style={{ width: '100%' }}>
                                  {online && (
                                    <Box
                                      style={{
                                        width: '8px',
                                        height: '8px',
                                        borderRadius: '50%',
                                        backgroundColor: status === 'disponivel' ? '#4CAF50' :
                                                        status === 'aguardando' ? '#2196F3' : '#FF9800',
                                        flexShrink: 0
                                      }}
                                    />
                                  )}
                                  <Typography variant="body2" style={{ flex: 1 }}>
                                    {entr.Nome}
                                  </Typography>
                                </Stack>
                              </MenuItem>
                            );
                          })}
                      </Select>
                    </FormControl>
                  )}
                </Observer>
              </Grid>
            </Grid>

            <Grid container direction="row" spacing={1} style={{ marginTop: 24, marginBottom: 8 }}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={marcarACaminho}
                      onChange={handleChangeMarcarACaminho}
                      color="primary"
                      size="medium"
                    />
                  }
                  label="Marcar entrega como 'A Caminho'"
                  style={{ marginLeft: 0 }}
                />
              </Grid>
            </Grid>

          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button variant="contained" color="primary" onClick={enviar} disabled={entregador === ""}>
            Enviar
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openRota}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleCloseRota}
        aria-describedby="alert-dialog-slide-description"
      >
        <DialogTitle>Compartilhar rota</DialogTitle>
        <DialogContent>
          {/* <DialogContentText id="selecionar-entregador">
            Lista de entregadores
          </DialogContentText> */}
          <Box sx={{ minWidth: 300 }} style={{ height: 45 }}>
            <Observer>
              {() => (
                <TextField
                  disabled
                  id="outlined-disabled"
                  fullWidth
                  value={mapaStore.linkRota}
                />
              )}
            </Observer>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRota}>Fechar</Button>
          <Button onClick={() => { navigator.clipboard.writeText(mapaStore.linkRota) }}>Copiar</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
